import { FC, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateFormat } from 'utils/shared/utils';
import Dropdown from '../../Dropdown';
import Button from 'components/ui/Button/Button';
import Rating from 'components/ui/Rating/Rating';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { formatPriceCurrency, getCurrency, getLogoUrl, showError, showSuccess, showWarning } from 'utils/utils';
import { validate } from 'utils/validation';
import useRecaptcha from 'hooks/useRecaptcha';
import { createProposal } from 'services/cms';
import ProposalModal from '../Create/ProposalNew';
import {
  FlProposal,
  HotProposal,
  TripSlotRequirement,
} from 'ts/shared/interfaces/CreateProposalParams';
import { getBounds } from 'utils/shared/utils';
import { availableLanguages, DEFAULT_CANCELLATION_DATE_DELTA, tripBuilderAdditionalInfoValidation } from 'constants/constant';
import { orderGroupColors } from 'utils/shared/my-order';
import {
  buildTripBuilderCreateParams,
  buildTripSlotContexts,
  emptyTripBuilderActiveState,
  filterDisplayedProposal,
  getHotelKey,
  getFlightKey,
  parseTripBuilderSelectionsFromExternalParam,
  getTransferKey,
  tripRowKeys,
  defaultTripSlotRequirement,
  TripBuilderAdditionalInfoValues,
  mapProposalToTripBuilderAdditionalInfo,
  buildTripBuilderAdditionalInfoParams,
  getTripBuilderFirstServiceDateFromDraft,
  migrateRecordKey,
  migrateTransitionKeys,
  getCity,
  getCheckIn,
  getCheckOut,
  getHotelSlotKey,
  getSlotKey,
  getBeforeFlightData,
  getAfterFlightData,
  getFlightLegs,
} from 'utils/shared/tripBuilder';

import TripBuilderHotelImage from './TripBuilderHotelImage';
import { applyProposalCurrency, getActiveProposalCurrency, resolveProposalLanguageFromBranch } from 'utils/shared/proposal';
import { getBlobUrl } from 'utils/shared/tripBuilderImages';
import TripBuilderAdditionalInfoModal, { TripBuilderAdditionalInfoProposalSettings } from './TripBuilderAdditionalInfoModal';
import { getProposalGallery } from 'services/proposal';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import TripBuilderHeaderInfo from './TripBuilderHeaderInfo';
import ProposalHotelDataModal, { ProposalHotelDataTab } from '../Single/ProposalHotelDataModal';
import { applyDraftPayloadToIdbHotel, mapHotProposalToModalHotel } from 'utils/shared/tripResultBuilderIdb';
import { TripBuilderHotelDataDraftPayload } from 'utils/shared/tripResultBuilderPreview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faPencil, faRobot } from '@fortawesome/free-solid-svg-icons';
import FreeTextModal from '../Single/FreeTextModal';
import CityNameModal from '../CityNameModal';
import { saveProposal as saveProposalToIdb } from 'utils/shared/idbUrl';
import { MainContext } from 'contexts/MainContext';
import { gIco, trIco } from 'constants/generic';

type FlightSegment = 'before' | 'middle' | 'after';

type FlightCandidate = {
  flight: FlProposal;
  index: number;
  route: string;
  isRoundTrip: boolean;
  airlineCode?: string;
};

const getCompoundFlightBackground = (color: string) => `color-mix(in srgb, ${color} 20%, white)`;

const buildCompoundFlightColorMap = (
  beforeCandidates: FlightCandidate[],
  afterCandidates: FlightCandidate[],
  middleFlightsByTransition: Record<string, FlightCandidate[]>
) => {
  const map = new Map<string, string>();
  let colorIndex = 0;
  const assignColor = (candidate: FlightCandidate) => {
    if (!candidate.isRoundTrip) {
      return;
    }
    const key = getFlightKey(candidate.flight, candidate.index);
    if (map.has(key)) {
      return;
    }
    map.set(key, orderGroupColors[colorIndex % orderGroupColors.length]);
    colorIndex += 1;
  };

  beforeCandidates.forEach(assignColor);
  Object.values(middleFlightsByTransition).forEach((candidates) => candidates.forEach(assignColor));
  afterCandidates.forEach(assignColor);
  return map;
};

const getFirstPriceSegmentForFlight = (
  flightKey: string,
  ctx: {
    beforeCandidates: FlightCandidate[];
    afterCandidates: FlightCandidate[];
    middleFlightsByTransition: Record<string, FlightCandidate[]>;
  }
): { segment: FlightSegment; transitionKey?: string } | null => {
  if (ctx.beforeCandidates.some((candidate) => getFlightKey(candidate.flight, candidate.index) === flightKey)) {
    return { segment: 'before' };
  }
  const transitionKeys = Object.keys(ctx.middleFlightsByTransition);
  for (let index = 0; index < transitionKeys.length; index += 1) {
    const transitionKey = transitionKeys[index];
    if (
      ctx.middleFlightsByTransition[transitionKey].some(
        (candidate) => getFlightKey(candidate.flight, candidate.index) === flightKey
      )
    ) {
      return { segment: 'middle', transitionKey };
    }
  }
  if (ctx.afterCandidates.some((candidate) => getFlightKey(candidate.flight, candidate.index) === flightKey)) {
    return { segment: 'after' };
  }
  return null;
};

const shouldShowFlightPrice = (
  flightKey: string,
  isRoundTrip: boolean,
  segment: FlightSegment,
  transitionKey: string | undefined,
  ctx: {
    beforeCandidates: FlightCandidate[];
    afterCandidates: FlightCandidate[];
    middleFlightsByTransition: Record<string, FlightCandidate[]>;
  }
) => {
  if (!isRoundTrip) {
    return true;
  }
  const firstSegment = getFirstPriceSegmentForFlight(flightKey, ctx);
  if (!firstSegment) {
    return false;
  }
  if (firstSegment.segment === 'middle') {
    return segment === 'middle' && transitionKey === firstSegment.transitionKey;
  }
  return segment === firstSegment.segment;
};

type TripSlot = {
  key: string;
  city: string;
  checkIn: string;
  checkOut: string;
};

type EditingCitySlot = TripSlot;

const updateHotelCityName = (hotel: HotProposal, cityName: string): HotProposal => ({
  ...hotel,
  item: {
    ...hotel.item,
    info: {
      ...hotel.item?.info,
      cityName,
    },
    addressInfo: {
      ...hotel.item?.addressInfo,
      city: cityName,
    },
  },
});

type TripBoundaryDate = {
  day: string;
  month: string;
  weekDay: string;
};

const slotRequirementOptions: Array<{ id: TripSlotRequirement; label: string }> = [
  { id: 'optional', label: 'Optional' },
  { id: 'mandatory', label: 'Mandatory' },
  { id: 'mandatory-first', label: 'Mandatory First' },
];

const getRequirementLabel = (value: TripSlotRequirement) =>
  slotRequirementOptions.find((option) => option.id === value)?.label ?? 'Mandatory';

const SlotRequirementDropdown: FC<{
  value: TripSlotRequirement;
  onChange: (value: TripSlotRequirement) => void;
  dateBadge?: TripBoundaryDate;
}> = ({ value, onChange, dateBadge }) => {
  const [closeOnClickDate, setCloseOnClickDate] = useState<Date>();

  return (
    <div className="pr-tb-status flex-column">
      {!!dateBadge && (
        <div className="pr-tb-badge flex-v-center">
          <span className="pr-tb-badge-day text-bold">{dateBadge.day}</span>
          <span className="pr-tb-badge-meta flex-column text-xsm">
            <span className="pr-tb-badge-month text-bold">{dateBadge.month}</span>
            <span className="pr-tb-badge-weekday">{dateBadge.weekDay}</span>
          </span>
        </div>
      )}
      <Dropdown
        byClick
        openerEmpty
        top={30}
        left={0}
        right={-20}
        align="left"
        closeOnClickDate={closeOnClickDate}
        dropdownClassName="pr-tb-status-menu text-xxs"
        opener={
          <button type="button" className="btn btn-default btn-sm" style={{ minWidth: 130 }}>
            {getRequirementLabel(value)}
          </button>
        }
      >
        {slotRequirementOptions.map((option) => (
          <div
            className="c-dropdown-row"
            key={option.id}
            onClick={() => {
              onChange(option.id);
              setCloseOnClickDate(new Date());
            }}
          >
            {option.label}
          </div>
        ))}
      </Dropdown>
    </div>
  );
};

const TripOptionRow: FC<{
  value: TripSlotRequirement;
  onChange: (value: TripSlotRequirement) => void;
  children: ReactNode;
  dateBadge?: TripBoundaryDate;
}> = ({ value, onChange, children, dateBadge }) => (
  <div className="pr-tb-option display-flex">
    <SlotRequirementDropdown value={value} onChange={onChange} dateBadge={dateBadge} />
    <div className="pr-tb-option-body flex-column">{children}</div>
  </div>
);

const TripHotelOptionRow: FC<{
  value: TripSlotRequirement;
  onChange: (value: TripSlotRequirement) => void;
  imageUrl?: string;
  city?: string;
  onImageUrlChange: (url: string | undefined) => void;
  children: ReactNode;
}> = ({ value, onChange, imageUrl, city, onImageUrlChange, children }) => (
  <div className="pr-tb-option display-flex">
    <div className="pr-tb-status-col flex-column">
      <TripBuilderHotelImage
        imageUrl={imageUrl}
        city={city}
        onImageUrlChange={(url) => {
          onImageUrlChange(url);
        }}
      />
      <SlotRequirementDropdown value={value} onChange={onChange} />

    </div>
    <div className="pr-tb-option-body flex-column">{children}</div>
  </div>
);

const TripBuilderDraftHotelCard: FC<{
  hotel: HotProposal;
  isActive: boolean;
  onSelect: () => void;
  getStartsFrom: (hotel: HotProposal) => string;
  onUpdateHotel: (searchToken: string, updater: (hotel: HotProposal) => HotProposal) => void;
  defaultShowCancellationDate: boolean;
  defaultCancellationDateDelta: number;
}> = ({
  hotel,
  isActive,
  onSelect,
  getStartsFrom,
  onUpdateHotel,
  defaultShowCancellationDate,
  defaultCancellationDateDelta,
}) => {
    const { t } = useTranslation();
    const [showHotelDataPopup, setShowHotelDataPopup] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [hotelDataTab, setHotelDataTab] = useState<ProposalHotelDataTab>('google');
    const currentHotel = useMemo(() => mapHotProposalToModalHotel(hotel), [hotel]);
    const hasAiHotelInfo = !!currentHotel?.aI_HotelInformatiom?.content?.length;
    const hasChosenRooms = !!hotel.item?.choosenRoomIds?.length;

    const onSourceClick =
      (handler?: () => void) => (event: React.MouseEvent) => {
        event.stopPropagation();
        handler?.();
      };

    const onSaveHotelDataDraft = (payload: TripBuilderHotelDataDraftPayload) => {
      onUpdateHotel(hotel.searchToken, (current) => applyDraftPayloadToIdbHotel(current, payload));
      setShowHotelDataPopup(false);
    };

    const onSaveFreeText = (
      desc: string,
      displayOrder?: number,
      cancellationSettings?: { showCancellationDate: boolean; cancellationDateDelta: number },
    ) => {
      onUpdateHotel(hotel.searchToken, (current) => ({
        ...current,
        freeText: desc,
        displayOrder,
        ...(cancellationSettings
          ? {
            showCancellationDate: cancellationSettings.showCancellationDate,
            cancellationDateDelta: cancellationSettings.cancellationDateDelta,
          }
          : {}),
      }));
      setShowEditModal(false);
    };

    return (
      <>
        <div className="pr-tb-card-wrap">
          <button
            type="button"
            className={'pr-tb-card flex-column' + (isActive ? ' is-active' : '')}
            style={{ height: 'auto' }}
            onClick={onSelect}
          >
            <Tooltip content={hotel.item.item.name} noPlague>
              <div className="pr-tb-card-title text-sm text-bold">{hotel.item.item.name}</div>
            </Tooltip>
            {!!hotel.item?.item?.star && (
              <Rating value={hotel.item.item.star} className="pr-tb-card-rating" />
            )}
            <div className="pr-tb-card-price flex-between text-xsm text-bold text-black">
              <span>
                {!hasChosenRooms && (
                  <span
                    className="color-danger text-normal cur-poi text-xsm"
                    onClick={(event) => {
                      event.stopPropagation();
                      window.dispatchEvent(new CustomEvent('proposal:open-details-tab'));
                    }}
                  >
                    Choose Room
                  </span>
                )}
              </span>
              <span className="text-black">From {getStartsFrom(hotel)}</span>
            </div>
          </button>
          <div className="pr-tb-card-actions">
            <span className="position-relative">
              <Tooltip content={t('hotel.info')}>
                <div
                  className="display-flex cur-poi pr-source-btn"
                  onClick={onSourceClick(() => {
                    setHotelDataTab('hotel_info');
                    setShowHotelDataPopup(true);
                  })}
                >
                  <FontAwesomeIcon icon={faInfoCircle} style={{ width: 16, height: 16 }} size="sm" />
                </div>
              </Tooltip>
            </span>
            {hasAiHotelInfo && (
              <span className="position-relative">
                <Tooltip content={t('proposal.ai_hotel_info')}>
                  <div
                    className="display-flex cur-poi pr-source-btn"
                    onClick={onSourceClick(() => {
                      setHotelDataTab('ai_hotel_info');
                      setShowHotelDataPopup(true);
                    })}
                  >
                    <FontAwesomeIcon icon={faRobot} style={{ width: 16, height: 16 }} size="sm" />
                  </div>
                </Tooltip>
              </span>
            )}
            <span className="position-relative">
              <Tooltip content={t('hotel.google')}>
                <div
                  className="display-flex cur-poi pr-source-btn"
                  onClick={onSourceClick(() => {
                    setHotelDataTab('google');
                    setShowHotelDataPopup(true);
                  })}
                >
                  <img src={gIco} alt="g" className="pr-source-btn-img" />
                </div>
              </Tooltip>
            </span>
            <span className="position-relative">
              <Tooltip content={t('hotel.tripadvisor')}>
                <div
                  className="display-flex cur-poi pr-source-btn"
                  onClick={onSourceClick(() => {
                    setHotelDataTab('tripadvisor');
                    setShowHotelDataPopup(true);
                  })}
                >
                  <img src={trIco} alt="t" className="pr-source-btn-img" />
                </div>
              </Tooltip>
            </span>
            <span className="position-relative">
              <Tooltip content={t('proposal.edit')}>
                <div
                  className="display-flex cur-poi pr-source-btn"
                  onClick={onSourceClick(() => setShowEditModal(true))}
                >
                  <FontAwesomeIcon icon={faPencil} style={{ width: 16, height: 16 }} size="sm" />
                </div>
              </Tooltip>
            </span>
          </div>
        </div>
        {showHotelDataPopup && (
          <ProposalHotelDataModal
            show={showHotelDataPopup}
            onClose={() => setShowHotelDataPopup(false)}
            hotelDataTab={hotelDataTab}
            setHotelDataTab={setHotelDataTab}
            currentHotel={currentHotel}
            onSaveDraft={onSaveHotelDataDraft}
          />
        )}
        {showEditModal && (
          <FreeTextModal
            close={() => setShowEditModal(false)}
            onSave={onSaveFreeText}
            freeText={hotel.freeText || ''}
            displayOrder={hotel.displayOrder}
            isHotel
            showCancellationDate={hotel.showCancellationDate ?? defaultShowCancellationDate}
            cancellationDateDelta={hotel.cancellationDateDelta ?? defaultCancellationDateDelta}
          />
        )}
      </>
    );
  };

interface TripBuilderProps {
  onProposalCreated?: (proposal: ProposalData) => void;
}

export const TripBuilder: FC<TripBuilderProps> = ({ onProposalCreated }) => {
  const { t } = useTranslation();
  const { user, proposal, setProposal } = useContext(MainContext);
  const blobUrl = getBlobUrl();
  const { handleRecapcha } = useRecaptcha();
  const defaultLanguage = resolveProposalLanguageFromBranch(user?.branch?.ext_1_pcc);
  const [language, setLanguage] = useState(defaultLanguage);
  const [showAdditionalInfoModal, setShowAdditionalInfoModal] = useState(false);
  const [additionalInfoShowValidation, setAdditionalInfoShowValidation] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState<TripBuilderAdditionalInfoValues>(() =>
    mapProposalToTripBuilderAdditionalInfo(proposal.existingProposal as any),
  );
  const additionalInfoOpenedByValidationRef = useRef(false);
  const pendingAdditionalInfoRef = useRef<TripBuilderAdditionalInfoValues | null>(null);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [activeTransferBySlot, setActiveTransferBySlot] = useState<Record<string, string>>({});
  const [requirementByRow, setRequirementByRow] = useState<Record<string, TripSlotRequirement>>(
    () => proposal.tripBuilder?.rows ?? {},
  );
  const [displayTotalDelta, setDisplayTotalDelta] = useState(
    () => proposal.tripBuilder?.displayTotalDelta ?? false,
  );
  const [showCancellationDate, setShowCancellationDate] = useState(
    () => proposal.tripBuilder?.showCancellationDate ?? proposal.hotels[0]?.showCancellationDate ?? true,
  );
  const [cancellationDateDelta, setCancellationDateDelta] = useState(() =>
    String(
      proposal.tripBuilder?.cancellationDateDelta ??
      proposal.hotels[0]?.cancellationDateDelta ??
      DEFAULT_CANCELLATION_DATE_DELTA,
    ),
  );

  useEffect(() => {
    void getProposalGallery();
  }, []);

  useEffect(() => {
    setRequirementByRow(proposal.tripBuilder?.rows ?? {});
    setDisplayTotalDelta(proposal.tripBuilder?.displayTotalDelta ?? false);
    setShowCancellationDate(
      proposal.tripBuilder?.showCancellationDate ?? proposal.hotels[0]?.showCancellationDate ?? true,
    );
    setCancellationDateDelta(
      String(
        proposal.tripBuilder?.cancellationDateDelta ??
        proposal.hotels[0]?.cancellationDateDelta ??
        DEFAULT_CANCELLATION_DATE_DELTA,
      ),
    );
  }, [proposal.tripBuilder, proposal.hotels]);

  useEffect(() => {
    const restored =
      parseTripBuilderSelectionsFromExternalParam(proposal.existingProposal?.externalParam_1) ||
      emptyTripBuilderActiveState;
    setActiveHotelBySlot(restored.activeHotelBySlot);
    setActiveTransferBySlot(restored.activeTransferBySlot || {});
    setActiveBeforeFlightBySlot(restored.activeBeforeFlightBySlot);
    setActiveAfterFlightBySlot(restored.activeAfterFlightBySlot);
    setActiveMiddleFlightByTransition(restored.activeMiddleFlightByTransition);
    setHotelImageBySlot(restored.hotelImageBySlot || {});
    setProposalImage(restored.proposalImage || '');
  }, [proposal.existingProposal?.externalParam_1]);

  useEffect(() => {
    setHotelImageBySlot(proposal.tripBuilder?.hotelImageBySlot ?? {});
    setProposalImage(proposal.tripBuilder?.proposalImage || '');
  }, [proposal.tripBuilder?.hotelImageBySlot, proposal.tripBuilder?.proposalImage]);

  const [activeHotelBySlot, setActiveHotelBySlot] = useState<Record<string, string>>({});
  const [activeBeforeFlightBySlot, setActiveBeforeFlightBySlot] = useState<Record<string, string>>({});
  const [activeAfterFlightBySlot, setActiveAfterFlightBySlot] = useState<Record<string, string>>({});
  const [activeMiddleFlightByTransition, setActiveMiddleFlightByTransition] = useState<
    Record<string, string>
  >({});
  const [hotelImageBySlot, setHotelImageBySlot] = useState<Record<string, string>>(
    () => proposal.tripBuilder?.hotelImageBySlot ?? {},
  );
  const [proposalImage, setProposalImage] = useState(
    () => proposal.tripBuilder?.proposalImage || '',
  );
  const [editingCitySlot, setEditingCitySlot] = useState<EditingCitySlot | null>(null);

  const slots = useMemo<TripSlot[]>(() => {
    const map = new Map<string, TripSlot>();
    proposal.hotels.forEach((hotel) => {
      const city = getCity(hotel);
      const checkIn = getCheckIn(hotel);
      const checkOut = getCheckOut(hotel);
      const key = getHotelSlotKey(hotel);
      if (!map.has(key)) {
        map.set(key, { key, city, checkIn, checkOut });
      }
    });
    return Array.from(map.values());
  }, [proposal.hotels]);

  const formatSlotDate = (value: string) => (value ? dateFormat(value, 'dd MMM yyyy', 'en') : '-');
  const formatTransferDateTime = (value: string) => (value ? dateFormat(value, 'ddMMM HH:mm', 'en') : '');

  const formatFlightSchedule = (departureDateTime?: string, arrivalDateTime?: string) => {
    if (!departureDateTime || !arrivalDateTime) {
      return '';
    }
    const depDate = dateFormat(departureDateTime, 'ddMMM', 'en').toUpperCase();
    const depTime = dateFormat(departureDateTime, 'HH:mm', 'en');
    const arrTime = dateFormat(arrivalDateTime, 'HH:mm', 'en');
    const arrDateSuffix =
      ` ${dateFormat(arrivalDateTime, 'ddMMM', 'en').toUpperCase()}`;
    return `${depDate} ${depTime} - ${arrTime}${arrDateSuffix}`;
  };

  const getFlightCardBound = (flight: FlProposal, segment: 'before' | 'after' | 'middle', route?: string) => {
    const bounds = getBounds(flight.item);
    if (!bounds.length) {
      return undefined;
    }
    let bound = bounds[0];
    if (segment === 'after') {
      bound = bounds[bounds.length - 1];
    } else if (segment === 'middle' && route) {
      const matchedBound = bounds.find(
        (candidate: any) =>
          `${candidate?.departureAirport?.code || '-'} -> ${candidate?.arrivalAirport?.code || '-'}` === route
      );
      if (matchedBound) {
        bound = matchedBound;
      }
    }
    return bound;
  };

  const getFlightCardSchedule = (flight: FlProposal, segment: 'before' | 'after' | 'middle', route?: string) => {
    const bound = getFlightCardBound(flight, segment, route);
    if (!bound) {
      return '';
    }
    return formatFlightSchedule(bound?.departureDateTime, bound?.arrivalDateTime);
  };

  const getFlightStopsLabel = (flight: FlProposal, segment: 'before' | 'after' | 'middle', route?: string) => {
    const bound = getFlightCardBound(flight, segment, route);
    const stopsCount = Math.max(0, (bound?.gdsFlyAirs?.length || 1) - 1);
    if (!stopsCount) {
      return 'Direct';
    }
    return `${stopsCount} stops`;
  };

  const formatBoundaryDate = (value?: string): TripBoundaryDate | undefined => {
    if (!value) {
      return undefined;
    }
    return {
      day: dateFormat(value, 'dd', 'en'),
      month: dateFormat(value, 'MMM', 'en').toUpperCase(),
      weekDay: dateFormat(value, 'EEE', 'en').toUpperCase(),
    };
  };

  const getFlightBoundaryDate = (flight: FlProposal | undefined, boundary: 'start' | 'finish') => {
    if (!flight?.item) {
      return '';
    }
    const bounds = getBounds(flight.item);
    if (!bounds.length) {
      return '';
    }
    if (boundary === 'start') {
      return bounds[0]?.departureDateTime || '';
    }
    const last = bounds[bounds.length - 1];
    return last?.arrivalDateTime || last?.departureDateTime || '';
  };

  const getStartsFrom = (hotel: HotProposal) => {
    const prices = (hotel.item.roomClasses || [])
      .map((room: any) => room.price?.value)
      .filter((value: any): value is number => typeof value === 'number' && Number.isFinite(value));
    if (!prices.length) {
      return '-';
    }
    const minPrice = Math.min(...prices);
    return formatPriceCurrency(minPrice, getCurrency(hotel.currency || hotel.item.currency));
  };

  const updateHotel = (
    searchToken: string,
    updater: (hotel: HotProposal) => HotProposal,
  ) => {
    setProposal({
      ...proposal,
      hotels: proposal.hotels.map((hotel) =>
        hotel.searchToken === searchToken ? updater(hotel) : hotel,
      ),
    });
  };

  const onSaveCityName = (newCity: string) => {
    if (!editingCitySlot) {
      return;
    }

    const { key: slotKey, checkIn, checkOut } = editingCitySlot;
    const trimmedCity = newCity.trim();
    if (!trimmedCity) {
      return;
    }

    const newSlotKey = getSlotKey(trimmedCity, checkIn, checkOut);
    const updatedHotels = proposal.hotels.map((hotel) => {
      if (getHotelSlotKey(hotel) !== slotKey) {
        return hotel;
      }
      return updateHotelCityName(hotel, trimmedCity);
    });

    setProposal({ ...proposal, hotels: updatedHotels });

    if (newSlotKey !== slotKey) {
      setActiveHotelBySlot((prev) => migrateRecordKey(prev, slotKey, newSlotKey));
      setActiveTransferBySlot((prev) => migrateRecordKey(prev, slotKey, newSlotKey));
      setHotelImageBySlot((prev) => migrateRecordKey(prev, slotKey, newSlotKey));
      setActiveMiddleFlightByTransition((prev) => migrateTransitionKeys(prev, slotKey, newSlotKey));
      setRequirementByRow((prev) => {
        const next = { ...prev };
        const oldHotelRowKey = tripRowKeys.hotel(slotKey);
        const newHotelRowKey = tripRowKeys.hotel(newSlotKey);
        const oldTransferRowKey = tripRowKeys.transfer(slotKey);
        const newTransferRowKey = tripRowKeys.transfer(newSlotKey);

        if (next[oldHotelRowKey] !== undefined) {
          next[newHotelRowKey] = next[oldHotelRowKey];
          delete next[oldHotelRowKey];
        }
        if (next[oldTransferRowKey] !== undefined) {
          next[newTransferRowKey] = next[oldTransferRowKey];
          delete next[oldTransferRowKey];
        }
        return next;
      });
    }

    setEditingCitySlot(null);
  };

  const flightModels = useMemo(
    () =>
      proposal.flights.map((flight, index) => ({
        flight,
        index,
        beforeData: getBeforeFlightData(flight),
        afterData: getAfterFlightData(flight),
        legs: getFlightLegs(flight),
      })),
    [proposal.flights]
  );

  const slotContexts = useMemo(() => buildTripSlotContexts(proposal), [proposal]);

  const proposalTitle = useMemo(() => {
    const firstWithSuitable = slotContexts.find((context) => context.suitableRoundTrips.length > 0);
    if (firstWithSuitable) {
      const firstTrip = firstWithSuitable.suitableRoundTrips[0];
      return `Proposal - ${firstTrip.beforeData.route}; ${firstTrip.afterData.route}`;
    }
    const fallbackRoundTrip = flightModels.find((flightModel) => flightModel.beforeData.isRoundTrip);
    if (!fallbackRoundTrip) {
      return 'Proposal';
    }
    return `Proposal - ${fallbackRoundTrip.beforeData.route}; ${fallbackRoundTrip.afterData.route}`;
  }, [slotContexts, flightModels]);

  const fullName = useMemo(
    () => [additionalInfo.firstName, additionalInfo.lastName].filter(Boolean).join(' ').trim(),
    [additionalInfo.firstName, additionalInfo.lastName],
  );

  const firstServiceDate = useMemo(
    () => getTripBuilderFirstServiceDateFromDraft(proposal),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [proposal.hotels, proposal.flights, proposal.transfers],
  );

  const getRowRequirement = (rowKey: string, defaultValue: TripSlotRequirement) =>
    requirementByRow[rowKey] ?? proposal.tripBuilder?.rows?.[rowKey] ?? defaultValue;

  const setRowRequirement = (rowKey: string, value: TripSlotRequirement) => {
    setRequirementByRow((prev) => ({ ...prev, [rowKey]: value }));
  };

  const buildTripBuilderRows = () => {
    const rows: Record<string, TripSlotRequirement> = { ...proposal.tripBuilder?.rows };

    slotContexts.forEach(
      ({ rootSlotKey, chainNodes, beforeCandidates, afterCandidates, middleFlightsByTransition }) => {
        if (beforeCandidates.length) {
          const rowKey = tripRowKeys.before(rootSlotKey);
          rows[rowKey] = getRowRequirement(rowKey, defaultTripSlotRequirement);
        }
        chainNodes.forEach((node) => {
          if (node.hotels.length) {
            const rowKey = tripRowKeys.hotel(node.slot.key);
            rows[rowKey] = getRowRequirement(rowKey, defaultTripSlotRequirement);
          }
          if (node.transfers.length) {
            const rowKey = tripRowKeys.transfer(node.slot.key);
            rows[rowKey] = getRowRequirement(rowKey, defaultTripSlotRequirement);
          }
        });
        Object.entries(middleFlightsByTransition).forEach(([transitionKey, candidates]) => {
          if (candidates.length) {
            const rowKey = tripRowKeys.middle(transitionKey);
            rows[rowKey] = getRowRequirement(rowKey, 'optional');
          }
        });
        if (afterCandidates.length) {
          const rowKey = tripRowKeys.after(rootSlotKey);
          rows[rowKey] = getRowRequirement(rowKey, defaultTripSlotRequirement);
        }
      }
    );

    return rows;
  };

  const onClickSave = async (override?: {
    additionalInfo?: TripBuilderAdditionalInfoValues;
    proposalSettings?: TripBuilderAdditionalInfoProposalSettings;
  }) => {
    const info = override?.additionalInfo ?? additionalInfo;
    const isValid = validate(tripBuilderAdditionalInfoValidation, info);
    if (!isValid) {
      additionalInfoOpenedByValidationRef.current = true;
      setAdditionalInfoShowValidation(true);
      setShowAdditionalInfoModal(true);
      return;
    }

    setLoadingCreate(true);

    const rows = buildTripBuilderRows();
    const slotContextsForSave = buildTripSlotContexts(proposal);
    const nextShowCancellationDate =
      override?.proposalSettings?.showCancellationDate ?? showCancellationDate;
    const nextDisplayTotalDelta =
      override?.proposalSettings?.displayTotalDelta ?? displayTotalDelta;
    const nextCancellationDateDelta =
      override?.proposalSettings?.cancellationDateDelta ?? cancellationDateDelta;
    const nextProposalImage = override?.proposalSettings?.proposalImage ?? proposalImage;

    const token = await handleRecapcha();
    if (!token) {
      setLoadingCreate(false);
      return;
    }

    const filteredProposal = filterDisplayedProposal(proposal, slotContextsForSave);
    const targetCurrency = getActiveProposalCurrency(filteredProposal);
    const proposalToSave = await applyProposalCurrency(filteredProposal, targetCurrency);

    const hotelMissingRooms = proposalToSave.hotels.some(
      (hotel) => !hotel.item?.choosenRoomIds?.length,
    );
    if (hotelMissingRooms) {
      showWarning(
        'Each hotel must have at least one room selected. You can choose rooms on the Details tab.',
      );
      setLoadingCreate(false);
      return;
    }

    setRequirementByRow(rows);

    const params = {
      ...buildTripBuilderCreateParams(
        proposalToSave,
        rows,
        nextDisplayTotalDelta,
        {
          comments: 'Have a nice trip!',
          recaptchaToken: token,
          inputData: JSON.stringify({
            user: user?.proposalUserInfo,
            branchLogo: user?.branch?.logoName || '',
            nameFromFax: user?.nameFromFax,
            template: 't1',
            language,
            sort: '1',
            activeHotelBySlot,
            activeTransferBySlot,
            activeBeforeFlightBySlot,
            activeAfterFlightBySlot,
            activeMiddleFlightByTransition,
            isTripBuilder: true,
            proposalImage: nextProposalImage,
            claudeHotelInfoConfig: JSON.stringify(
              Object.fromEntries(
                proposalToSave.hotels
                  .filter((hotel) => hotel.claudeHotelInfoConfig)
                  .map((hotel) => [hotel.item.itemCode, hotel.claudeHotelInfoConfig]),
              ),
            ),
            hotelInfoConfig: JSON.stringify(
              Object.fromEntries(
                proposalToSave.hotels
                  .filter((hotel) => hotel.hotelInfoConfig)
                  .map((hotel) => [hotel.item.itemCode, hotel.hotelInfoConfig]),
              ),
            ),
          }),
          description: proposalTitle,
        },
        hotelImageBySlot,
        {
          showCancellationDate: nextShowCancellationDate,
          cancellationDateDelta: +nextCancellationDateDelta,
          language,
        },
      ),
      ...buildTripBuilderAdditionalInfoParams(info),
    };

    const response = await createProposal(params);
    const emptyProposal = { hotels: [], flights: [], dynamic: [], ilPackages: [], transfers: [] };

    if (response && !response.hasErrors && !response.sessionExpired) {
      showSuccess(t('proposal.created'));
      setRequirementByRow({});
      await saveProposalToIdb('proposal', emptyProposal);
      if (response.proposal) {
        setProposal({
          ...emptyProposal,
          existingProposal: response.proposal,
        });
      } else {
        setProposal(emptyProposal);
      }
    } else if (response?.sessionExpired) {
      setProposal(emptyProposal);
      showError(t('session_expired_try_again'));
    } else if (response?.hasErrors) {
      showError(t('something_went_wrong'));
    } else {
      showError(t('something_went_wrong'));
    }

    setLoadingCreate(false);
  };

  return (
    <div className="pr-tb" dir="ltr">
      <div className="pr-tb-main">
        {!!slots.length && (
          <div className="pr-tb-head">
            <TripBuilderHeaderInfo
              proposalId={proposal.existingProposal?.id}
              clientName={fullName}
              firstServiceDate={firstServiceDate}
              expireDate={additionalInfo.expireDate}
              onEditAdditionalInfo={() => setShowAdditionalInfoModal(true)}
            />
          </div>
        )}
        {!slots.length && <div className="pr-tb-empty flex-center text-xsm">No hotels in proposal yet</div>}
        {!!slots.length && (
          <div className="pr-tb-slots flex-column">
            {slotContexts.map(
              ({
                rootSlotKey,
                chainNodes,
                beforeCandidates,
                afterCandidates,
                middleFlightsByTransition,
              }) => {
                const flightCtx = {
                  beforeCandidates,
                  afterCandidates,
                  middleFlightsByTransition,
                };
                const compoundFlightColorByKey = buildCompoundFlightColorMap(
                  beforeCandidates,
                  afterCandidates,
                  middleFlightsByTransition
                );
                const activeBeforeFlight = beforeCandidates.find(
                  (candidate) =>
                    getFlightKey(candidate.flight, candidate.index) === activeBeforeFlightBySlot[rootSlotKey]
                );
                const activeAfterFlight = afterCandidates.find(
                  (candidate) =>
                    getFlightKey(candidate.flight, candidate.index) === activeAfterFlightBySlot[rootSlotKey]
                );
                const firstNode = chainNodes[0];
                const lastNode = chainNodes[chainNodes.length - 1];
                const activeStartHotel = firstNode
                  ? firstNode.hotels.find(
                    (item, index) => getHotelKey(item, index) === activeHotelBySlot[firstNode.slot.key]
                  )
                  : undefined;
                const activeFinishHotel = lastNode
                  ? lastNode.hotels.find(
                    (item, index) => getHotelKey(item, index) === activeHotelBySlot[lastNode.slot.key]
                  )
                  : undefined;
                const startDateBadge = formatBoundaryDate(
                  getFlightBoundaryDate(
                    (activeBeforeFlight || beforeCandidates[0])?.flight,
                    'start'
                  ) || getCheckIn(activeStartHotel || firstNode?.hotels[0])
                );
                const finishDateBadge = formatBoundaryDate(
                  getFlightBoundaryDate(
                    (activeAfterFlight || afterCandidates[0])?.flight,
                    'finish'
                  ) || getCheckOut(activeFinishHotel || lastNode?.hotels[0])
                );

                return (
                  <div className="pr-tb-slot flex-column" key={rootSlotKey}>
                    {!!beforeCandidates.length && (
                      <TripOptionRow
                        value={getRowRequirement(tripRowKeys.before(rootSlotKey), defaultTripSlotRequirement)}
                        onChange={(value) => setRowRequirement(tripRowKeys.before(rootSlotKey), value)}
                        dateBadge={startDateBadge}
                      >
                        <div className="pr-tb-row display-flex">
                          {beforeCandidates.map((flightModel) => {
                            const flightKey = getFlightKey(flightModel.flight, flightModel.index);
                            const isActive = activeBeforeFlightBySlot[rootSlotKey] === flightKey;
                            const compoundColor = flightModel.isRoundTrip
                              ? compoundFlightColorByKey.get(flightKey)
                              : undefined;
                            const showPrice =
                              shouldShowFlightPrice(
                                flightKey,
                                flightModel.isRoundTrip,
                                'before',
                                undefined,
                                flightCtx
                              );
                            const flightSchedule = getFlightCardSchedule(flightModel.flight, 'before');
                            const stopsLabel = getFlightStopsLabel(flightModel.flight, 'before');
                            return (
                              <button
                                type="button"
                                className={
                                  'pr-tb-card flex-column' +
                                  (isActive ? ' is-active' : '') +
                                  (compoundColor ? ' is-compound' : '')
                                }
                                style={
                                  compoundColor
                                    ? { backgroundColor: getCompoundFlightBackground(compoundColor) }
                                    : undefined
                                }
                                key={`${rootSlotKey}-before-${flightKey}`}
                                onClick={() => {
                                  if (isActive) {
                                    setActiveBeforeFlightBySlot((prev) => {
                                      const next = { ...prev };
                                      delete next[rootSlotKey];
                                      return next;
                                    });
                                    setActiveAfterFlightBySlot((prev) => {
                                      const next = { ...prev };
                                      if (next[rootSlotKey] === flightKey) {
                                        delete next[rootSlotKey];
                                      }
                                      return next;
                                    });
                                    setActiveMiddleFlightByTransition((prev) => {
                                      const next = { ...prev };
                                      Object.keys(next).forEach((key) => {
                                        if (next[key] === flightKey) {
                                          delete next[key];
                                        }
                                      });
                                      return next;
                                    });
                                    return;
                                  }
                                  if (!flightModel.isRoundTrip) {
                                    setActiveBeforeFlightBySlot((prev) => {
                                      const next = { ...prev };
                                      const current = next[rootSlotKey];
                                      const currentIsRoundTrip = beforeCandidates.some(
                                        (candidate) =>
                                          getFlightKey(candidate.flight, candidate.index) === current &&
                                          candidate.isRoundTrip
                                      );
                                      if (currentIsRoundTrip) {
                                        delete next[rootSlotKey];
                                      }
                                      return next;
                                    });
                                    setActiveAfterFlightBySlot((prev) => {
                                      const next = { ...prev };
                                      const current = next[rootSlotKey];
                                      const currentIsRoundTrip = afterCandidates.some(
                                        (candidate) =>
                                          getFlightKey(candidate.flight, candidate.index) === current &&
                                          candidate.isRoundTrip
                                      );
                                      if (currentIsRoundTrip) {
                                        delete next[rootSlotKey];
                                      }
                                      return next;
                                    });
                                    Object.entries(middleFlightsByTransition).forEach(
                                      ([transitionKey, middleCandidates]) => {
                                        setActiveMiddleFlightByTransition((prev) => {
                                          const next = { ...prev };
                                          const current = next[transitionKey];
                                          const currentIsRoundTrip = middleCandidates.some(
                                            (candidate) =>
                                              getFlightKey(candidate.flight, candidate.index) === current &&
                                              candidate.isRoundTrip
                                          );
                                          if (currentIsRoundTrip) {
                                            delete next[transitionKey];
                                          }
                                          return next;
                                        });
                                      }
                                    );
                                  }
                                  setActiveBeforeFlightBySlot((prev) => ({
                                    ...prev,
                                    [rootSlotKey]: flightKey,
                                  }));
                                  if (
                                    afterCandidates.some(
                                      (candidate) =>
                                        getFlightKey(candidate.flight, candidate.index) === flightKey
                                    )
                                  ) {
                                    setActiveAfterFlightBySlot((prev) => ({
                                      ...prev,
                                      [rootSlotKey]: flightKey,
                                    }));
                                  }
                                  Object.entries(middleFlightsByTransition).forEach(
                                    ([transitionKey, middleCandidates]) => {
                                      if (
                                        middleCandidates.some(
                                          (candidate) =>
                                            getFlightKey(candidate.flight, candidate.index) === flightKey
                                        )
                                      ) {
                                        setActiveMiddleFlightByTransition((prev) => ({
                                          ...prev,
                                          [transitionKey]: flightKey,
                                        }));
                                      }
                                    }
                                  );
                                }}
                              >
                                <div className="pr-tb-flight-head">
                                  {!!flightModel.flight.item.outbound?.airline?.code && (
                                    <div className="pr-tb-flight-logo">
                                    <img
                                        className="pr-tb-flight-logo-img"
                                        src={getLogoUrl(
                                          blobUrl,
                                          flightModel.flight.item.outbound?.airline?.code
                                        )}
                                        alt={flightModel.flight.item.outbound?.airline?.code}
                                      />
                                    </div>
                                  )}
                                  <div className="pr-tb-card-title text-sm text-bold">
                                    <span>{flightModel.route}</span>
                                    <span className="text-light text-xsm text-normal"> ({stopsLabel})</span>
                                  </div>
                                </div>
                                <div
                                  className={
                                    'pr-tb-card-price flex-between text-xsm text-bold' +
                                    (showPrice || flightSchedule ? '' : ' is-empty')
                                  }
                                >
                                  {!!flightSchedule && (
                                    <span className="pr-tb-flight-time text-xxs text-bold">{flightSchedule}</span>
                                  )}
                                  <span className="pr-tb-card-amount text-black">
                                    {showPrice && formatPriceCurrency(
                                      flightModel.flight.price,
                                      getCurrency(flightModel.flight.currency)

                                    )}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </TripOptionRow>
                    )}

                    {chainNodes.map((node, nodeIndex) => {
                      const nextNode = chainNodes[nodeIndex + 1];
                      const transitionKey = nextNode ? `${node.slot.key}|${nextNode.slot.key}` : '';
                      const middleFlights = transitionKey
                        ? middleFlightsByTransition[transitionKey] || []
                        : [];

                      return (
                        <div className="pr-tb-block flex-column" key={`${rootSlotKey}-node-${node.slot.key}`}>
                          <div className="pr-tb-meta flex-between">
                            <span className="pr-tb-city text-md text-bold">{node.slot.city} <Tooltip content={t('proposal.edit')}>
                              <span
                                className="display-inline-flex cur-poi color-contrast ml-5"
                                onClick={() =>
                                  setEditingCitySlot({
                                    key: node.slot.key,
                                    city: node.slot.city,
                                    checkIn: node.slot.checkIn,
                                    checkOut: node.slot.checkOut,
                                  })
                                }
                              >
                                <FontAwesomeIcon icon={faPencil} style={{ width: 14, height: 14 }} size="sm" />
                              </span>
                            </Tooltip></span>

                            <span className="pr-tb-date text-xsm">
                              ({formatSlotDate(node.slot.checkIn)} - {formatSlotDate(node.slot.checkOut)})
                            </span>
                          </div>
                          {!node.hotels.length && <div className="pr-tb-plus flex-center">+</div>}
                          {!!node.hotels.length && (
                            <TripHotelOptionRow
                              imageUrl={hotelImageBySlot[node.slot.key]}
                              city={node.slot.city}
                              onImageUrlChange={(url) => {
                                setHotelImageBySlot((prev) => {
                                  const next = { ...prev };
                                  if (url) {
                                    next[node.slot.key] = url;
                                  } else {
                                    delete next[node.slot.key];
                                  }
                                  return next;
                                });
                              }}
                              value={getRowRequirement(tripRowKeys.hotel(node.slot.key), defaultTripSlotRequirement)}
                              onChange={(value) =>
                                setRowRequirement(tripRowKeys.hotel(node.slot.key), value)
                              }
                            >
                              <div className="pr-tb-row display-flex">
                                {node.hotels.map((hotel, index) => {
                                  const hotelKey = getHotelKey(hotel, index);
                                  const isActive = activeHotelBySlot[node.slot.key] === hotelKey;
                                  return (
                                    <TripBuilderDraftHotelCard
                                      key={`${rootSlotKey}-${node.slot.key}-${hotelKey}`}
                                      hotel={hotel}
                                      isActive={isActive}
                                      getStartsFrom={getStartsFrom}
                                      onUpdateHotel={updateHotel}
                                      defaultShowCancellationDate={showCancellationDate}
                                      defaultCancellationDateDelta={+cancellationDateDelta}
                                      onSelect={() => {
                                        if (isActive) {
                                          setActiveHotelBySlot((prev) => {
                                            const next = { ...prev };
                                            delete next[node.slot.key];
                                            return next;
                                          });
                                          return;
                                        }
                                        setActiveHotelBySlot((prev) => ({
                                          ...prev,
                                          [node.slot.key]: hotelKey,
                                        }));
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            </TripHotelOptionRow>
                          )}
                          {!!node.transfers.length && (
                            <TripOptionRow
                              value={getRowRequirement(tripRowKeys.transfer(node.slot.key), defaultTripSlotRequirement)}
                              onChange={(value) =>
                                setRowRequirement(tripRowKeys.transfer(node.slot.key), value)
                              }
                            >
                              <div className="pr-tb-row display-flex">
                                {node.transfers.map((transferModel) => {
                                  const transferKey = getTransferKey(transferModel.transfer, transferModel.index);
                                  const isActive = activeTransferBySlot[node.slot.key] === transferKey;
                                  return (
                                    <button
                                      type="button"
                                      className={'pr-tb-card flex-column' + (isActive ? ' is-active' : '')}
                                      key={`${rootSlotKey}-${node.slot.key}-transfer-${transferKey}`}
                                      onClick={() => {
                                        if (isActive) {
                                          setActiveTransferBySlot((prev) => {
                                            const next = { ...prev };
                                            delete next[node.slot.key];
                                            return next;
                                          });
                                          return;
                                        }
                                        setActiveTransferBySlot((prev) => ({
                                          ...prev,
                                          [node.slot.key]: transferKey,
                                        }));
                                      }}
                                    >
                                      <div className="pr-tb-card-title text-sm text-bold">
                                        <span>{transferModel.vehicle}</span>
                                      </div>
                                      <div className="pr-tb-card-price flex-between text-xsm text-bold">
                                        {!!formatTransferDateTime(transferModel.dateTime) && (
                                          <span className="pr-tb-flight-time text-xxs text-bold text-uppercase">
                                            {formatTransferDateTime(transferModel.dateTime)}
                                          </span>
                                        )}
                                        <span className="text-black">
                                          {formatPriceCurrency(
                                            transferModel.transfer.price,
                                            getCurrency(transferModel.transfer.currency)
                                          )}
                                        </span>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </TripOptionRow>
                          )}
                          {!!middleFlights.length && (
                            <TripOptionRow
                              value={getRowRequirement(tripRowKeys.middle(transitionKey), 'optional')}
                              onChange={(value) =>
                                setRowRequirement(tripRowKeys.middle(transitionKey), value)
                              }
                            >
                              <div className="pr-tb-row display-flex">
                                {middleFlights.map((flightModel) => {
                                  const middleKey = getFlightKey(flightModel.flight, flightModel.index);
                                  const isActive = activeMiddleFlightByTransition[transitionKey] === middleKey;
                                  const compoundColor = flightModel.isRoundTrip
                                    ? compoundFlightColorByKey.get(middleKey)
                                    : undefined;
                                  const showPrice =
                                    shouldShowFlightPrice(
                                      middleKey,
                                      flightModel.isRoundTrip,
                                      'middle',
                                      transitionKey,
                                      flightCtx
                                    );
                                  const flightSchedule = getFlightCardSchedule(
                                    flightModel.flight,
                                    'middle',
                                    flightModel.route
                                  );
                                  const stopsLabel = getFlightStopsLabel(
                                    flightModel.flight,
                                    'middle',
                                    flightModel.route
                                  );
                                  return (
                                    <button
                                      type="button"
                                      className={
                                        'pr-tb-card flex-column' +
                                        (isActive ? ' is-active' : '') +
                                        (compoundColor ? ' is-compound' : '')
                                      }
                                      style={
                                        compoundColor
                                          ? {
                                            backgroundColor: getCompoundFlightBackground(compoundColor),
                                          }
                                          : undefined
                                      }
                                      key={`${rootSlotKey}-middle-${transitionKey}-${middleKey}`}
                                      onClick={() => {
                                        if (isActive) {
                                          setActiveMiddleFlightByTransition((prev) => {
                                            const next = { ...prev };
                                            delete next[transitionKey];
                                            Object.keys(next).forEach((key) => {
                                              if (next[key] === middleKey) {
                                                delete next[key];
                                              }
                                            });
                                            return next;
                                          });
                                          setActiveBeforeFlightBySlot((prev) => {
                                            const next = { ...prev };
                                            if (next[rootSlotKey] === middleKey) {
                                              delete next[rootSlotKey];
                                            }
                                            return next;
                                          });
                                          setActiveAfterFlightBySlot((prev) => {
                                            const next = { ...prev };
                                            if (next[rootSlotKey] === middleKey) {
                                              delete next[rootSlotKey];
                                            }
                                            return next;
                                          });
                                          return;
                                        }
                                        setActiveMiddleFlightByTransition((prev) => ({
                                          ...prev,
                                          [transitionKey]: middleKey,
                                        }));
                                        if (
                                          beforeCandidates.some(
                                            (candidate) =>
                                              getFlightKey(candidate.flight, candidate.index) === middleKey
                                          )
                                        ) {
                                          setActiveBeforeFlightBySlot((prev) => ({
                                            ...prev,
                                            [rootSlotKey]: middleKey,
                                          }));
                                        }
                                        if (
                                          afterCandidates.some(
                                            (candidate) =>
                                              getFlightKey(candidate.flight, candidate.index) === middleKey
                                          )
                                        ) {
                                          setActiveAfterFlightBySlot((prev) => ({
                                            ...prev,
                                            [rootSlotKey]: middleKey,
                                          }));
                                        }
                                        Object.entries(middleFlightsByTransition).forEach(
                                          ([otherTransitionKey, middleCandidates]) => {
                                            if (
                                              middleCandidates.some(
                                                (candidate) =>
                                                  getFlightKey(candidate.flight, candidate.index) === middleKey
                                              )
                                            ) {
                                              setActiveMiddleFlightByTransition((prev) => ({
                                                ...prev,
                                                [otherTransitionKey]: middleKey,
                                              }));
                                            }
                                          }
                                        );
                                      }}
                                    >
                                      <div className="pr-tb-flight-head">
                                        {!!flightModel.airlineCode && (
                                          <div className="pr-tb-flight-logo">
                                    <img
                                        className="pr-tb-flight-logo-img"
                                              src={getLogoUrl(blobUrl, flightModel.airlineCode)}
                                              alt={flightModel.airlineCode}
                                            />
                                          </div>
                                        )}
                                        <div className="pr-tb-card-title text-sm text-bold">
                                          <span>{flightModel.route}</span>
                                          <span className="text-light text-xsm text-normal"> ({stopsLabel})</span>
                                        </div>
                                      </div>
                                      <div
                                        className={
                                          'pr-tb-card-price flex-between text-xsm text-bold' +
                                          (showPrice || flightSchedule ? '' : ' is-empty')
                                        }
                                      >
                                        {!!flightSchedule && (
                                          <span className="pr-tb-flight-time text-xxs text-bold">{flightSchedule}</span>
                                        )}
                                        {showPrice && (
                                          <span className="pr-tb-card-amount text-black">
                                            {formatPriceCurrency(
                                              flightModel.flight.price,
                                              getCurrency(flightModel.flight.currency)
                                            )}
                                          </span>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </TripOptionRow>
                          )}
                        </div>
                      );
                    })}

                    {!!afterCandidates.length && (
                      <TripOptionRow
                        value={getRowRequirement(tripRowKeys.after(rootSlotKey), defaultTripSlotRequirement)}
                        onChange={(value) => setRowRequirement(tripRowKeys.after(rootSlotKey), value)}
                        dateBadge={finishDateBadge}
                      >
                        <div className="pr-tb-row display-flex">
                          {afterCandidates.map((flightModel) => {
                            const flightKey = getFlightKey(flightModel.flight, flightModel.index);
                            const isActive = activeAfterFlightBySlot[rootSlotKey] === flightKey;
                            const compoundColor = flightModel.isRoundTrip
                              ? compoundFlightColorByKey.get(flightKey)
                              : undefined;
                            const showPrice =
                              shouldShowFlightPrice(
                                flightKey,
                                flightModel.isRoundTrip,
                                'after',
                                undefined,
                                flightCtx
                              );
                            const flightSchedule = getFlightCardSchedule(flightModel.flight, 'after');
                            const stopsLabel = getFlightStopsLabel(flightModel.flight, 'after');
                            return (
                              <button
                                type="button"
                                className={
                                  'pr-tb-card flex-column' +
                                  (isActive ? ' is-active' : '') +
                                  (compoundColor ? ' is-compound' : '')
                                }
                                style={
                                  compoundColor
                                    ? { backgroundColor: getCompoundFlightBackground(compoundColor) }
                                    : undefined
                                }
                                key={`${rootSlotKey}-after-${flightKey}`}
                                onClick={() => {
                                  if (isActive) {
                                    setActiveAfterFlightBySlot((prev) => {
                                      const next = { ...prev };
                                      delete next[rootSlotKey];
                                      return next;
                                    });
                                    setActiveBeforeFlightBySlot((prev) => {
                                      const next = { ...prev };
                                      if (next[rootSlotKey] === flightKey) {
                                        delete next[rootSlotKey];
                                      }
                                      return next;
                                    });
                                    setActiveMiddleFlightByTransition((prev) => {
                                      const next = { ...prev };
                                      Object.keys(next).forEach((key) => {
                                        if (next[key] === flightKey) {
                                          delete next[key];
                                        }
                                      });
                                      return next;
                                    });
                                    return;
                                  }
                                  if (!flightModel.isRoundTrip) {
                                    setActiveBeforeFlightBySlot((prev) => {
                                      const next = { ...prev };
                                      const current = next[rootSlotKey];
                                      const currentIsRoundTrip = beforeCandidates.some(
                                        (candidate) =>
                                          getFlightKey(candidate.flight, candidate.index) === current &&
                                          candidate.isRoundTrip
                                      );
                                      if (currentIsRoundTrip) {
                                        delete next[rootSlotKey];
                                      }
                                      return next;
                                    });
                                    setActiveAfterFlightBySlot((prev) => {
                                      const next = { ...prev };
                                      const current = next[rootSlotKey];
                                      const currentIsRoundTrip = afterCandidates.some(
                                        (candidate) =>
                                          getFlightKey(candidate.flight, candidate.index) === current &&
                                          candidate.isRoundTrip
                                      );
                                      if (currentIsRoundTrip) {
                                        delete next[rootSlotKey];
                                      }
                                      return next;
                                    });
                                    Object.entries(middleFlightsByTransition).forEach(
                                      ([transitionKey, middleCandidates]) => {
                                        setActiveMiddleFlightByTransition((prev) => {
                                          const next = { ...prev };
                                          const current = next[transitionKey];
                                          const currentIsRoundTrip = middleCandidates.some(
                                            (candidate) =>
                                              getFlightKey(candidate.flight, candidate.index) === current &&
                                              candidate.isRoundTrip
                                          );
                                          if (currentIsRoundTrip) {
                                            delete next[transitionKey];
                                          }
                                          return next;
                                        });
                                      }
                                    );
                                  }
                                  setActiveAfterFlightBySlot((prev) => ({
                                    ...prev,
                                    [rootSlotKey]: flightKey,
                                  }));
                                  if (
                                    beforeCandidates.some(
                                      (candidate) =>
                                        getFlightKey(candidate.flight, candidate.index) === flightKey
                                    )
                                  ) {
                                    setActiveBeforeFlightBySlot((prev) => ({
                                      ...prev,
                                      [rootSlotKey]: flightKey,
                                    }));
                                  }
                                  Object.entries(middleFlightsByTransition).forEach(
                                    ([transitionKey, middleCandidates]) => {
                                      if (
                                        middleCandidates.some(
                                          (candidate) =>
                                            getFlightKey(candidate.flight, candidate.index) === flightKey
                                        )
                                      ) {
                                        setActiveMiddleFlightByTransition((prev) => ({
                                          ...prev,
                                          [transitionKey]: flightKey,
                                        }));
                                      }
                                    }
                                  );
                                }}
                              >
                                <div className="pr-tb-flight-head">
                                  {!!flightModel.flight.item.outbound?.airline?.code && (
                                    <div className="pr-tb-flight-logo">
                                    <img
                                        className="pr-tb-flight-logo-img"
                                        src={getLogoUrl(
                                          blobUrl,
                                          flightModel.flight.item.outbound?.airline?.code
                                        )}
                                        alt={flightModel.flight.item.outbound?.airline?.code}
                                      />
                                    </div>
                                  )}
                                  <div className="pr-tb-card-title text-sm text-bold">
                                    <span>{flightModel.route}</span>
                                    <span className="text-light text-xsm text-normal"> ({stopsLabel})</span>
                                  </div>
                                </div>
                                <div
                                  className={
                                    'pr-tb-card-price flex-between text-xsm text-bold' +
                                    (showPrice || flightSchedule ? '' : ' is-empty')
                                  }
                                >
                                  {!!flightSchedule && (
                                    <span className="pr-tb-flight-time text-xxs text-bold">{flightSchedule}</span>
                                  )}
                                  <span className="pr-tb-card-amount text-black">
                                    {showPrice && formatPriceCurrency(
                                      flightModel.flight.price,
                                      getCurrency(flightModel.flight.currency)

                                    )}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </TripOptionRow>
                    )}
                  </div>
                );
              }
            )}
          </div>
        )}
        {!!slots.length && (
          <div className="pr-tb-footer flex-between">
            <div className="pr-tb-footer-row flex-v-center flex-wrap w100p">
              <div style={{ width: 180 }}>
                <SelectCustom
                  options={availableLanguages
                    .map((lang) => ({
                      label: lang.name,
                      value: lang.lang,
                      flag: lang.country,
                    }))
                    .filter((lang) => !(defaultLanguage === 'it' && lang.value === 'he'))}
                  value={language}
                  onChange={setLanguage}
                  menuPlacement="top"
                />
              </div>
              <Button
                className="btn btn-default"
                title="Additional info"
                onClick={() => {
                  additionalInfoOpenedByValidationRef.current = false;
                  setShowAdditionalInfoModal(true);
                }}
              />
              <div className="pr-tb-footer-actions flex-v-center">
                <Button
                  className="btn btn-primary"
                  title="Save Trip Builder"
                  disabled={loadingCreate}
                  onClick={() => {
                    void onClickSave();
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="pr-tb-side">
        <ProposalModal
          onProposalCreated={onProposalCreated}
          listenToAssistant={false}
          tripBuilderMode
        />
      </div>
      {showAdditionalInfoModal && (
        <TripBuilderAdditionalInfoModal
          values={additionalInfo}
          showValidation={additionalInfoShowValidation}
          close={() => {
            additionalInfoOpenedByValidationRef.current = false;
            pendingAdditionalInfoRef.current = null;
            setShowAdditionalInfoModal(false);
            setAdditionalInfoShowValidation(false);
          }}
          onSave={(values) => {
            pendingAdditionalInfoRef.current = values;
            setAdditionalInfo(values);
            setAdditionalInfoShowValidation(false);
          }}
          proposalSettings={{
            showCancellationDate,
            displayTotalDelta,
            cancellationDateDelta,
            proposalImage,
          }}
          onProposalSettingsSave={(settings) => {
            setShowCancellationDate(settings.showCancellationDate);
            setDisplayTotalDelta(settings.displayTotalDelta);
            setCancellationDateDelta(settings.cancellationDateDelta);
            setProposalImage(settings.proposalImage);

            if (!additionalInfoOpenedByValidationRef.current) {
              return;
            }

            additionalInfoOpenedByValidationRef.current = false;
            const info = pendingAdditionalInfoRef.current ?? additionalInfo;
            pendingAdditionalInfoRef.current = null;
            void onClickSave({ additionalInfo: info, proposalSettings: settings });
          }}
        />
      )}
      {editingCitySlot && (
        <CityNameModal
          close={() => setEditingCitySlot(null)}
          onSave={onSaveCityName}
          cityName={editingCitySlot.city}
        />
      )}
    </div>
  );
};